import React, {FC, useState} from 'react';
import {Box, Pagination, Paper, Typography} from '@mui/material';
import {useIntl} from 'react-intl';
import {DetailedTransactionCostDTO} from 'src/types/transactions';
import {formatPrice} from 'src/utils/helper';
import {formatLocationTime} from 'src/utils/locationTime';
import {CostItemsExportButton} from '../CostItemsExportButton';

interface CostTimelineProps {
    cost?: DetailedTransactionCostDTO;
    wattsConsumed: number;
    transactionId: number;
    /** The location's clock: the hour and day-of-week in every price snapshot are already local. */
    locationTimezone?: string;
}

const ITEMS_PER_PAGE = 10;

export const CostTimeline: FC<CostTimelineProps> = ({
                                                        cost,
                                                        wattsConsumed,
                                                        transactionId,
                                                        locationTimezone
                                                    }) => {
    const intl = useIntl();
    const [page, setPage] = useState(1);

    const hasItems = cost?.items && cost.items.length > 0;
    const totalItems = cost?.items?.length || 0;
    const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

    const consumptionKwh = wattsConsumed * 0.001;
    const totalCostValue = cost?.totalCost ? formatPrice(cost.totalCost) : '0';
    const averagePricePerKwh = cost?.averagePrice ? formatPrice(cost.averagePrice) : '0';

    const paginatedItems = cost?.items?.slice(
        (page - 1) * ITEMS_PER_PAGE,
        page * ITEMS_PER_PAGE
    ) || [];

    const formatConsumption = (watts: number) => {
        return `${(watts / 1000).toFixed(3)} kWh`;
    };

    const getCurrency = () => {
        return cost?.currency || 'CHF';
    };

    const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    const [showExportModal, setShowExportModal] = useState(false);

    const handleCardClick = () => {
        if (hasItems) {
            setShowExportModal(true);
        }
    };

    return (
        <Box>
            {/* Export Modal */}
            {hasItems && (
                <CostItemsExportButton
                    transactionId={transactionId}
                    showModal={showExportModal}
                    onClose={() => setShowExportModal(false)}
                />
            )}

            {/* Total Summary - Clickable Card */}
            <Paper
                onClick={hasItems ? handleCardClick : undefined}
                sx={{
                    padding: 2,
                    backgroundColor: '#32324D',
                    borderRadius: 2,
                    marginBottom: 3,
                    cursor: hasItems ? 'pointer' : 'default',
                    transition: 'all 0.2s',
                    position: 'relative',
                    '&:hover': hasItems ? {
                        backgroundColor: '#3a3a52',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        '& .export-label': {
                            opacity: 1,
                            visibility: 'visible',
                        },
                    } : {},
                }}
            >
                {hasItems && (
                    <Box
                        className="export-label"
                        sx={{
                            position: 'absolute',
                            bottom: '100%',
                            left: '50%',
                            transform: 'translateX(-50%) translateY(-8px)',
                            backgroundColor: 'rgba(58, 58, 82, 0.95)',
                            color: 'white',
                            padding: '6px 12px',
                            borderRadius: 1,
                            opacity: 0,
                            visibility: 'hidden',
                            transition: 'all 0.3s ease',
                            pointerEvents: 'none',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                            zIndex: 10,
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                top: '100%',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: 0,
                                height: 0,
                                borderLeft: '5px solid transparent',
                                borderRight: '5px solid transparent',
                                borderTop: '5px solid rgba(58, 58, 82, 0.95)',
                            },
                        }}
                    >
                        {intl.formatMessage({id: 'exportCosts'})}
                    </Box>
                )}
                <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 3}}>
                    <Box sx={{textAlign: 'center'}}>
                        <Typography variant="body2"
                                    sx={{color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.875rem', marginBottom: 0.5}}>
                            {intl.formatMessage({id: 'sessionConsumption'})}
                        </Typography>
                        <Typography variant="h5" fontWeight={700} sx={{color: 'white'}}>
                            {consumptionKwh.toFixed(3)} kWh
                        </Typography>
                    </Box>
                    <Box sx={{textAlign: 'center'}}>
                        <Typography variant="body2"
                                    sx={{color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.875rem', marginBottom: 0.5}}>
                            {intl.formatMessage({id: 'averageKwhPrice'})}
                        </Typography>
                        <Typography variant="h5" fontWeight={700} sx={{color: 'white'}}>
                            {averagePricePerKwh} {getCurrency()}/kWh
                        </Typography>
                    </Box>
                    <Box sx={{textAlign: 'center'}}>
                        <Typography variant="body2"
                                    sx={{color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.875rem', marginBottom: 0.5}}>
                            {intl.formatMessage({id: 'totalCost'})}
                        </Typography>
                        <Typography variant="h5" fontWeight={700} sx={{color: 'white'}}>
                            {totalCostValue} {getCurrency()}
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {/* Cost Items Details */}
            {hasItems && (
                <Box>
                    {paginatedItems.map((item, index) => {
                        const actualIndex = (page - 1) * ITEMS_PER_PAGE + index;
                        return (
                            <Paper
                                key={item.id}
                                sx={{
                                    padding: 0,
                                    marginBottom: 3,
                                    backgroundColor: 'background.paper',
                                    borderLeft: '4px solid',
                                    borderLeftColor: '#32324D',
                                    overflow: 'hidden',
                                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                                }}
                            >
                                <Box sx={{padding: 1.5}}>
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: 0.75
                                    }}>
                                        <Typography variant="body2" fontWeight={600} sx={{fontSize: '0.875rem'}}>
                                            {intl.formatMessage({id: 'item'})} #{actualIndex + 1}
                                        </Typography>
                                    </Box>

                                    <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 1.5}}>
                                        <Box>
                                            <Typography variant="caption" sx={{
                                                color: 'rgba(0, 0, 0, 0.6)',
                                                fontWeight: 500,
                                                fontSize: '0.7rem'
                                            }}>
                                                {intl.formatMessage({id: 'time'})}
                                                {locationTimezone ? ` (${locationTimezone})` : ''}
                                            </Typography>
                                            <Typography variant="body2" fontWeight={600}
                                                        sx={{color: '#32324D', fontSize: '0.8rem'}}>
                                                {formatLocationTime(item.itemTimestamp || item.meterValue?.valueTimestamp || item.createdAt, locationTimezone)}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" sx={{
                                                color: 'rgba(0, 0, 0, 0.6)',
                                                fontWeight: 500,
                                                fontSize: '0.7rem'
                                            }}>
                                                {intl.formatMessage({id: 'itemConsumption'})}
                                            </Typography>
                                            <Typography variant="body2" fontWeight={600}
                                                        sx={{color: '#32324D', fontSize: '0.8rem'}}>
                                                {formatConsumption(item.itemConsumption)}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" sx={{
                                                color: 'rgba(0, 0, 0, 0.6)',
                                                fontWeight: 500,
                                                fontSize: '0.7rem'
                                            }}>
                                                {intl.formatMessage({id: 'itemPrice'})}
                                            </Typography>
                                            <Typography variant="body2" fontWeight={600}
                                                        sx={{color: '#32324D', fontSize: '0.8rem'}}>
                                                {formatPrice(item.priceSnapshot?.itemPrice || 0)} {getCurrency()}/kWh
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" sx={{
                                                color: 'rgba(0, 0, 0, 0.6)',
                                                fontWeight: 500,
                                                fontSize: '0.7rem'
                                            }}>
                                                {intl.formatMessage({id: 'itemCost'})}
                                            </Typography>
                                            <Typography variant="body2" fontWeight={600}
                                                        sx={{color: '#32324D', fontSize: '0.8rem'}}>
                                                {formatPrice(item.itemCost)} {getCurrency()}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Price Details Strip */}
                                {item.priceSnapshot && (
                                    <Box
                                        sx={{
                                            borderTop: '1px solid',
                                            borderColor: 'divider',
                                            padding: '0px 16px',
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 1fr 1fr 1fr',
                                            gap: 2,
                                            backgroundColor: 'background.paper',
                                            alignItems: 'center',
                                            height: '16px',
                                        }}
                                    >
                                        <Box sx={{display: 'flex', alignItems: 'center', height: '100%'}}>
                                            <Typography variant="caption" sx={{
                                                color: 'rgba(0, 0, 0, 0.5)',
                                                fontSize: '0.65rem',
                                                lineHeight: '16px'
                                            }}>
                                                {item.priceSnapshot.month ? intl.formatMessage({id: item.priceSnapshot.month}) : '-'}
                                            </Typography>
                                        </Box>
                                        <Box sx={{display: 'flex', alignItems: 'center', height: '100%'}}>
                                            <Typography variant="caption" sx={{
                                                color: 'rgba(0, 0, 0, 0.5)',
                                                fontSize: '0.65rem',
                                                lineHeight: '16px'
                                            }}>
                                                {item.priceSnapshot.dayOfWeek ? intl.formatMessage({id: item.priceSnapshot.dayOfWeek}) : '-'}
                                            </Typography>
                                        </Box>
                                        <Box sx={{display: 'flex', alignItems: 'center', height: '100%'}}>
                                            <Typography variant="caption" sx={{
                                                color: 'rgba(0, 0, 0, 0.5)',
                                                fontSize: '0.65rem',
                                                lineHeight: '16px'
                                            }}>
                                                {item.priceSnapshot.hourOfDay !== null && item.priceSnapshot.hourOfDay !== undefined ? `${item.priceSnapshot.hourOfDay}:00` : '-'}
                                            </Typography>
                                        </Box>
                                        <Box sx={{display: 'flex', alignItems: 'center', height: '100%'}}>
                                            <Typography variant="caption" sx={{
                                                color: 'rgba(0, 0, 0, 0.5)',
                                                fontSize: '0.65rem',
                                                lineHeight: '16px'
                                            }}>
                                                {item.priceSnapshot.energyType ? intl.formatMessage({id: item.priceSnapshot.energyType}) : '-'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                            </Paper>
                        );
                    })}

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <Box sx={{display: 'flex', justifyContent: 'center', marginTop: 4}}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={handlePageChange}
                                color="primary"
                                size="large"
                                showFirstButton
                                showLastButton
                            />
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};